import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { persistFingerprintOnConsent } from '../../utils/fingerprint';

declare function gtag(command: 'consent', action: 'update', params: Record<string, string>): void;

const COOKIE_NAME = 'wedsy_cookie_consent';
const COOKIE_EXPIRY_DAYS = 365;

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = getCookie(COOKIE_NAME);
    if (!consent) {
      // Small delay before showing banner
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setCookie(COOKIE_NAME, 'accepted', COOKIE_EXPIRY_DAYS);
    setIsVisible(false);
    persistFingerprintOnConsent();
    try {
      if (typeof gtag !== 'undefined') {
        gtag('consent', 'update', { analytics_storage: 'granted' });
      }
    } catch {}
  };

  const handleDecline = () => {
    setCookie(COOKIE_NAME, 'declined', COOKIE_EXPIRY_DAYS);
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-background-card border border-border rounded-xl shadow-lg p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-text-primary mb-1">We use cookies</h3>
                  <p className="text-sm text-text-secondary">
                    We use essential cookies to keep Wedsy working. With your permission we'd also like to save a browser identifier to understand how visitors use our site across sessions — this helps us improve Wedsy and is never shared with third parties.{' '}
                    <a href="/privacy" className="text-primary hover:underline">
                      Learn more
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-auto">
                <button
                  onClick={handleDecline}
                  className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  Decline
                </button>
                <button
                  onClick={handleAccept}
                  className="px-5 py-2 text-sm font-medium bg-primary text-text-inverse rounded-full hover:bg-primary-hover transition-colors"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
